/**
 * Reusable TipTap email editor with toolbar.
 *
 * Used by:
 *   - Newsletter Editor (src/pages/Editor.jsx) — full-featured
 *   - Webcast Invite Modal (src/pages/Events.jsx) — compact mode
 */
import { useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExt from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import ImageExt from "@tiptap/extension-image";
import LinkExt from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link2,
  ImageIcon,
  Minus,
  Undo2,
  Redo2,
  Palette,
  RemoveFormatting,
  Zap,
  ChevronDown,
  Quote,
} from "lucide-react";
import { MERGE_TAGS } from "../styles/theme";
import { uploadImage } from "../services/api";
import { MergeTagNode } from "../extensions/MergeTagNode";
import "../styles/editor.css";

// ── Toolbar Button ──────────────────────────────────────────

export function ToolBtn({ icon: Icon, label, active, onClick, disabled, small }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      title={label}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: small ? 28 : 32,
        height: small ? 28 : 32,
        borderRadius: 5,
        border: "none",
        background: active
          ? "rgba(0,15,45,0.08)"
          : hover
            ? "rgba(0,0,0,0.04)"
            : "transparent",
        color: active
          ? "#000f2d"
          : disabled
            ? "#9ca3b4"
            : "#5a6178",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.12s",
      }}
    >
      <Icon size={small ? 14 : 16} />
    </button>
  );
}

export function ToolDivider() {
  return (
    <div style={{ width: 1, height: 20, background: "#e2e4ea", margin: "0 4px" }} />
  );
}

// ── Color Picker ────────────────────────────────────────────

const COLORS_PALETTE = [
  "#333333", "#1a1d26", "#5a6178", "#9ca3b4",
  "#ef4444", "#f59e0b", "#10b981", "#3b82f6",
  "#6366f1", "#ec4899", "#213D61", "#335D95",
];

export function ColorPicker({ onSelect, onClose }) {
  return (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        marginTop: 4,
        background: "#ffffff",
        border: "1px solid #e2e4ea",
        borderRadius: 8,
        padding: 10,
        boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
        zIndex: 200,
      }}
    >
      <div style={{ fontSize: 10, color: "#9ca3b4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 8 }}>
        Text Color
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
        {COLORS_PALETTE.map((color) => (
          <button
            key={color}
            onClick={() => { onSelect(color); onClose(); }}
            style={{
              width: 28, height: 28, borderRadius: 4,
              border: `2px solid ${color === "#333333" ? "#e2e4ea" : "transparent"}`,
              background: color, cursor: "pointer", transition: "transform 0.1s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main EmailEditor Component ──────────────────────────────

const EmailEditor = forwardRef(function EmailEditor(
  { content = "", onUpdate, placeholder = "Start writing...", mergeTags, compact },
  ref,
) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showMergeTags, setShowMergeTags] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const tags = mergeTags || MERGE_TAGS;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      UnderlineExt,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      ImageExt.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: { style: "max-width:100%;height:auto;display:block;" },
      }),
      LinkExt.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
      MergeTagNode,
    ],
    content,
    editorProps: {
      attributes: { spellcheck: "true" },
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false;
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        const imageFile = Array.from(files).find(f => f.type.startsWith("image/"));
        if (!imageFile) return false;
        if (imageFile.size > 5 * 1024 * 1024) {
          alert("Image must be under 5 MB");
          return true;
        }
        const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
        setUploading(true);
        uploadImage(imageFile).then(({ url }) => {
          const { schema } = view.state;
          const node = schema.nodes.image.create({ src: url, alt: imageFile.name });
          const pos = coordinates?.pos ?? view.state.selection.from;
          view.dispatch(view.state.tr.insert(pos, node));
        }).catch(err => {
          alert(`Upload failed: ${err.message}`);
        }).finally(() => {
          setUploading(false);
        });
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      if (onUpdate) onUpdate(ed.getHTML());
    },
  });

  // Expose editor instance to parent
  useImperativeHandle(ref, () => ({
    getHTML: () => editor?.getHTML() || "",
    getEditor: () => editor,
    setContent: (html) => editor?.commands.setContent(html),
  }), [editor]);

  // Merge tag insertion
  const insertMergeTag = useCallback((tag) => {
    if (!editor) return;
    const cleanTag = tag.replace(/[{}]/g, "");
    const mt = tags.find((m) => m.tag === tag);
    editor.chain().focus().insertContent({
      type: "mergeTag",
      attrs: { tag: cleanTag, label: mt?.label || cleanTag },
    }).run();
  }, [editor, tags]);

  // Link insertion
  const insertLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Enter URL:", previousUrl || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  }, [editor]);

  // Image upload
  const handleImageUpload = useCallback(async (file) => {
    if (!editor || !file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return; }
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }, [editor]);

  const handleInsertImage = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/gif,image/webp";
    input.onchange = (e) => { const file = e.target.files?.[0]; if (file) handleImageUpload(file); };
    input.click();
  }, [handleImageUpload]);

  // Drag-and-drop visual state (upload handled by editorProps.handleDrop)
  const handleEditorDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  // Block format
  const handleBlockFormat = useCallback((value) => {
    if (!editor) return;
    if (value === "p") editor.chain().focus().setParagraph().run();
    else if (value === "h1") editor.chain().focus().toggleHeading({ level: 1 }).run();
    else if (value === "h2") editor.chain().focus().toggleHeading({ level: 2 }).run();
    else if (value === "h3") editor.chain().focus().toggleHeading({ level: 3 }).run();
    else if (value === "blockquote") editor.chain().focus().toggleBlockquote().run();
  }, [editor]);

  const currentBlock = editor?.isActive("heading", { level: 1 }) ? "h1"
    : editor?.isActive("heading", { level: 2 }) ? "h2"
    : editor?.isActive("heading", { level: 3 }) ? "h3"
    : editor?.isActive("blockquote") ? "blockquote"
    : "p";

  if (!editor) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      {/* Toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 2,
        padding: compact ? "6px 12px" : "8px 16px",
        background: "#ffffff", borderBottom: "1px solid #e2e4ea",
        flexWrap: "wrap", flexShrink: 0,
      }}>
        <ToolBtn icon={Undo2} label="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} small={compact} />
        <ToolBtn icon={Redo2} label="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} small={compact} />
        <ToolDivider />

        <select
          value={currentBlock}
          onChange={(e) => handleBlockFormat(e.target.value)}
          style={{
            padding: "4px 8px", background: "#f8f9fb", border: "1px solid #e2e4ea",
            borderRadius: 4, color: "#5a6178", fontSize: 12,
            fontFamily: "'DM Sans', -apple-system, sans-serif", outline: "none", cursor: "pointer",
          }}
        >
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="blockquote">Quote</option>
        </select>
        <ToolDivider />

        <ToolBtn icon={Bold} label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} small={compact} />
        <ToolBtn icon={Italic} label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} small={compact} />
        <ToolBtn icon={Underline} label="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} small={compact} />
        <ToolBtn icon={Strikethrough} label="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} small={compact} />
        <ToolDivider />

        <div style={{ position: "relative" }}>
          <ToolBtn icon={Palette} label="Text Color" onClick={() => setShowColorPicker(!showColorPicker)} small={compact} />
          {showColorPicker && <ColorPicker onSelect={(color) => editor.chain().focus().setColor(color).run()} onClose={() => setShowColorPicker(false)} />}
        </div>
        <ToolDivider />

        <ToolBtn icon={AlignLeft} label="Align Left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} small={compact} />
        <ToolBtn icon={AlignCenter} label="Align Center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} small={compact} />
        <ToolBtn icon={AlignRight} label="Align Right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} small={compact} />
        <ToolDivider />

        <ToolBtn icon={List} label="Bullet List" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} small={compact} />
        <ToolBtn icon={ListOrdered} label="Numbered List" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} small={compact} />
        <ToolBtn icon={Quote} label="Blockquote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} small={compact} />
        <ToolDivider />

        <ToolBtn icon={Link2} label="Insert Link" onClick={insertLink} small={compact} />
        <ToolBtn icon={ImageIcon} label="Insert Image" onClick={handleInsertImage} disabled={uploading} small={compact} />
        <ToolBtn icon={Minus} label="Horizontal Rule" onClick={() => editor.chain().focus().setHorizontalRule().run()} small={compact} />
        <ToolDivider />

        {/* Merge Tags dropdown */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setShowMergeTags(!showMergeTags)}
            style={{
              display: "flex", alignItems: "center", gap: 4,
              padding: "4px 10px", borderRadius: 5,
              border: "1px solid #e2e4ea", background: "rgba(0,15,45,0.08)",
              color: "#000f2d", fontSize: 11, fontWeight: 700,
              fontFamily: "'DM Sans', -apple-system, sans-serif", cursor: "pointer",
            }}
          >
            <Zap size={12} /> Merge Tags <ChevronDown size={10} />
          </button>
          {showMergeTags && (
            <div style={{
              position: "absolute", top: "100%", left: 0, marginTop: 4,
              background: "#ffffff", border: "1px solid #e2e4ea", borderRadius: 8,
              padding: 6, minWidth: 240, boxShadow: "0 12px 40px rgba(0,0,0,0.15)", zIndex: 200,
            }}>
              <div style={{ padding: "6px 10px", fontSize: 10, color: "#9ca3b4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
                Insert Merge Tag
              </div>
              {tags.map((mt) => (
                <button
                  key={mt.tag}
                  onClick={() => { insertMergeTag(mt.tag); setShowMergeTags(false); }}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    width: "100%", padding: "8px 10px", border: "none", background: "transparent",
                    borderRadius: 4, cursor: "pointer", color: "#1a1d26", fontSize: 13,
                    fontFamily: "'DM Sans', -apple-system, sans-serif",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f1f4")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <span>{mt.label}</span>
                  <code style={{ fontSize: 11, color: "#000f2d", fontFamily: "'JetBrains Mono', monospace", background: "rgba(0,15,45,0.08)", padding: "1px 6px", borderRadius: 3 }}>
                    {mt.tag}
                  </code>
                </button>
              ))}
            </div>
          )}
        </div>

        <ToolBtn icon={RemoveFormatting} label="Clear Formatting" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} small={compact} />
      </div>

      {/* Editor Content */}
      <div
        style={{ flex: 1, overflow: "auto", background: "#e8eaed", display: "flex", justifyContent: "center", padding: compact ? "16px 12px" : "24px 20px" }}
        onDrop={handleEditorDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
      >
        <div
          style={{
            width: "100%", maxWidth: 720, background: "#ffffff",
            borderRadius: 8, boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          }}
          className={dragOver ? "editor-drop-active" : ""}
        >
          {uploading && (
            <div style={{ padding: "8px 16px", background: "rgba(0,15,45,0.08)", borderRadius: 6, marginBottom: 12, fontSize: 12, color: "#000f2d", textAlign: "center" }}>
              Uploading image...
            </div>
          )}
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
});

export default EmailEditor;
